import { useState, useEffect } from "react";
import { Col, Container, Form, Image, Row } from "react-bootstrap";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useBetween } from "use-between";
import AwareButton from "../../components/button";

import { CloseEye, Eye } from "../../components/icons";
import useApi from "../useApi";
import "./index.scss";

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    
    // Extract values from query parameters
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const userId = searchParams.get('userId');

    const { resetPassword } = useBetween(useApi);

    useEffect(() => {
        // Redirect if required query parameters are missing
        if (!token && !userId) {
            toast.error("Invalid reset password link. Please request a new password reset.");
            navigate("/forgot-password");
        }
    }, [token, userId, navigate]);

    const validateForm = () => {
        const errors = {};

        if (!newPassword.trim()) {
            errors.newPassword = "New password is required";
        } else if (newPassword.length < 8) {
            errors.newPassword = "New password must be at least 8 characters long";
        }

        if (!confirmPassword.trim()) {
            errors.confirmPassword = "Please confirm your new password";
        } else if (newPassword !== confirmPassword) {
            errors.confirmPassword = "Passwords do not match";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (field, value) => {
        if (field === 'newPassword') {
            setNewPassword(value);
        } else if (field === 'confirmPassword') {
            setConfirmPassword(value);
        }

        // Clear validation error for the field being updated
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const resetData = {
                newPassword: newPassword,
                confirmationCode: token,
            };

            // If we have userId, use the existing updatePassword method
            await resetPassword(resetData);

            toast.success("Password has been reset successfully!");
            navigate("/login");
        } catch (error) {
            toast.error(error?.message || "Failed to reset password. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container fluid className='login-container'>
            <Row className='vh-100'>
                <Col
                    md={5}
                    className='px-0 login-banner vh-100 d-none d-md-block position-relative'
                >
                    <Image
                        src='https://mammoth-product-images.s3.us-west-1.amazonaws.com/Images/login/login-screen.png'
                        alt='background'
                        fluid
                    />
                </Col>
                <Col className='login-form-wrapper'>
                    <Form
                        className='h-100 d-flex align-items-center'
                        onSubmit={handleSubmit}
                    >
                        <Row className='login-form-inner'>
                            <Col sm={12}>
                                <h1>Reset Your Password</h1>
                            </Col>
                            <Col sm={12}>
                                <p>Please enter your new password below.</p>
                            </Col>
                            <Col sm={12}>
                                <Form.Group className='form-group'>
                                    <Form.Label>New Password</Form.Label>
                                    <Form.Control
                                        type={showNewPassword ? "text" : "password"}
                                        value={newPassword}
                                        onChange={(event) =>
                                            handleInputChange('newPassword', event.target.value)
                                        }
                                        placeholder='Enter new password'
                                        required
                                        isInvalid={!!validationErrors.newPassword}
                                    />
                                    <Form.Label className='icon'>
                                        <span
                                            onClick={() =>
                                                setShowNewPassword(!showNewPassword)
                                            }
                                        >
                                            {showNewPassword ? (
                                                <Eye width='24' height='24' />
                                            ) : (
                                                <CloseEye
                                                    width='24'
                                                    height='24'
                                                />
                                            )}
                                        </span>
                                    </Form.Label>
                                    {validationErrors.newPassword && (
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.newPassword}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col sm={12}>
                                <Form.Group className='form-group'>
                                    <Form.Label>Confirm New Password</Form.Label>
                                    <Form.Control
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(event) =>
                                            handleInputChange('confirmPassword', event.target.value)
                                        }
                                        placeholder='Confirm new password'
                                        required
                                        isInvalid={!!validationErrors.confirmPassword}
                                    />
                                    <Form.Label className='icon'>
                                        <span
                                            onClick={() =>
                                                setShowConfirmPassword(!showConfirmPassword)
                                            }
                                        >
                                            {showConfirmPassword ? (
                                                <Eye width='24' height='24' />
                                            ) : (
                                                <CloseEye
                                                    width='24'
                                                    height='24'
                                                />
                                            )}
                                        </span>
                                    </Form.Label>
                                    {validationErrors.confirmPassword && (
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.confirmPassword}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col sm={12} className='text-center'>
                                <Link
                                    to={"/login"}
                                    className='btn-forgot'
                                >
                                    Back to Login
                                </Link>
                                <Link
                                    className='btn-create'
                                    to='/request-access'
                                >
                                    Request Access
                                </Link>
                            </Col>
                            <Col sm={12}>
                                <AwareButton
                                    type='submit'
                                    className='w-100 primary btn-primary'
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Resetting..." : "Reset Password"}
                                </AwareButton>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default ResetPassword;
